import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Box, TextField } from '@mui/material';
import { MessageList } from './MessageList';
import { tokens } from './tokens';
import { streamCopilotQuery } from '../../../services/copilotService';
import type { CopilotAnswer, ChatMessage, CopilotContext, CopilotScope } from './copilotTypes';
import { DEMO_CACHE, normalizeQuestion, suggestFollowups } from './demoCache';

const HISTORY_MAX_TURNS = 20; // 10 user→assistant pairs (mirrors the backend sanitizer cap)
// Cap the RENDERED transcript so the number of live ECharts canvases stays bounded (ECharts is
// canvas2d — the limit is memory, not WebGL contexts). Older turns drop off the top of the view;
// they're gone from the DOM (and so any chart they held is disposed) but the conversation kept
// flowing — history is capped separately by HISTORY_MAX_TURNS.
const TRANSCRIPT_RENDER_CAP = 12; // ~6 user→assistant pairs kept on screen

let _id = 0;
const newId = () => `m${++_id}`;

/**
 * Compact descriptor an assistant turn carries into `history` so the model recalls what it showed —
 * and, for chart turns, can REFINE it. We append the full chart spec (no data rows) so a follow-up
 * like "make it 90 days" lets the model carry the prior spec over and change only the asked-for field.
 */
function describeAnswer(answer: CopilotAnswer | undefined): string {
  if (!answer) return '';
  const prose = (answer as any).narrative || '';
  if (answer.kind === 'chart') {
    const spec = answer.spec as any;
    const metric = spec.metric ?? spec.xMetric ?? spec.source ?? (spec.metrics && spec.metrics[0]) ?? '';
    const range = typeof spec.range === 'string' ? spec.range : `${spec.range?.from}..${spec.range?.to}`;
    return `${prose} [chart: ${spec.chart} of ${metric}, ${range}]\n[previously rendered: ${JSON.stringify(spec)}]`.trim();
  }
  if (answer.kind === 'analysis') {
    return `${prose} [analysis: ${answer.packet.findings.map((finding) => finding.type).join(', ')}]\n[previously analyzed: ${JSON.stringify(answer.context?.plan || {})}]`.trim();
  }
  return prose;
}

/**
 * Builds a capped, role-alternating `history` from prior COMPLETE user→assistant pairs only.
 * A user turn whose assistant errored/cancelled is dropped (never splice a middle turn — that would
 * collapse to consecutive same-role turns and break Anthropic alternation).
 */
function buildHistory(messages: ChatMessage[]): Array<{ role: string; content: string }> {
  const history: Array<{ role: string; content: string }> = [];
  for (let i = 0; i < messages.length - 1; i++) {
    const user = messages[i];
    const assistant = messages[i + 1];
    if (user.role !== 'user' || assistant.role !== 'assistant') continue;
    if (assistant.status !== 'done') continue;
    history.push({ role: 'user', content: user.text });
    history.push({ role: 'assistant', content: describeAnswer(assistant.answer) || assistant.text });
    i++; // consumed the pair
  }
  return history.slice(-HISTORY_MAX_TURNS);
}

/**
 * The Copilot panel: a multi-turn chat over a study's data. Each question appends a user turn plus a
 * streaming assistant turn; the narrative streams into that turn's bubble and the answer (catalog chart,
 * generative escape hatch, or table) mounts under it. Every question hits the live backend (current data,
 * conversational refinement); the local cache is only a fallback when the live request errors.
 */
export function CopilotTab({ studyId }: { studyId: string }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [conversationContext, setConversationContext] = useState<CopilotContext | undefined>(undefined);
  const abortRef = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  const contextRef = useRef<CopilotContext | undefined>(undefined);
  messagesRef.current = messages;
  contextRef.current = conversationContext;
  const streaming = messages.some((m) => m.status === 'streaming');

  /** Reset the chat session: clear all messages, context, and abort any in-flight request. */
  const resetChat = () => {
    abortRef.current?.abort();
    setMessages([]);
    setConversationContext(undefined);
    setInput('');
  };
  // Gate the cached fallback's word-stream timer: under reduced motion the narrative lands at once
  // instead of typing in word by word. Read here (hook) and used in renderCached below.
  const reduceMotion = useReducedMotion();

  // On study switch: drop the transcript and abort any in-flight request (no cross-study leak).
  useEffect(() => {
    setMessages([]);
    setConversationContext(undefined);
    abortRef.current?.abort();
  }, [studyId]);

  // Scope a state update to a specific assistant turn in this study; a stale/aborted request whose
  // turn is gone (studyId changed) is a no-op.
  const patch = (id: string, fn: (m: ChatMessage) => ChatMessage) =>
    setMessages((prev) => {
      if (!prev.some((m) => m.id === id)) return prev;
      return prev.map((m) => (m.id === id ? fn(m) : m));
    });

  /**
   * Renders a cached scripted beat into the bubble — used ONLY as a fallback when the live request
   * errors. Word-chunks the cached narrative so a scripted beat streams like a live one, then mounts
   * its answer tagged as cache-sourced.
   */
  async function renderCached(assistantId: string, cached: CopilotAnswer) {
    const narrative = (cached as any).narrative || '';
    // Reduced motion: skip the per-word timer entirely (no rAF/timeout chain) — the bubble jumps to
    // the full narrative, then mounts the answer below.
    if (!reduceMotion) {
      const words: string[] = narrative.split(/(\s+)/); // keep the whitespace tokens so spacing survives
      for (let i = 0; i < words.length; i++) {
        const slice = words.slice(0, i + 1).join('');
        patch(assistantId, (m) => ({ ...m, text: slice }));
        await new Promise((r) => setTimeout(r, 18));
      }
    }
    patch(assistantId, (m) => ({
      ...m,
      text: narrative,
      answer: cached,
      scope: (cached as any).scope,
      suggestions: suggestFollowups(cached),
      meta: { source: 'cache' },
      // Clear any count left by the FAILED live attempt — the cached chart's data is its own, so a
      // stale "N records" from the failed query must never lead it.
      count: undefined,
      status: 'done',
    }));
  }

  /**
   * Runs one assistant turn into an existing assistant bubble. LIVE-FIRST: every question hits the
   * backend so charts reflect current data and can be conversationally refined. The scripted DEMO_CACHE
   * is only a FALLBACK — if the live request errors and a cached beat exists for this question, the
   * cached answer is rendered in place of an error turn.
   */
  async function run(assistantId: string, question: string, history: Array<{ role: string; content: string }>, requestContext?: CopilotContext) {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let acc = '';
    let resolved = false;
    let errored = false;
    await streamCopilotQuery(
      { studyId, question, history, ...(requestContext ? { context: requestContext } : {}) },
      {
        onToken: (d) => { acc += d; patch(assistantId, (m) => ({ ...m, text: acc })); },
        // The in-range record count (just before a chart result) drives the instrument readout; a `null`
        // (restart/abort/fallback) clears it so a count never precedes a non-chart/fallback result.
        onCount: (count) => patch(assistantId, (m) => ({ ...m, count: count ?? undefined })),
        onProgress: (progress) => patch(assistantId, (m) => ({ ...m, progress })),
        onRestart: () => { acc = ''; patch(assistantId, (m) => ({ ...m, text: '' })); },
        onResult: (a: CopilotAnswer) => {
          resolved = true;
          const n = (a as any).narrative;
          // Prose/clarify render their narrative inside AnswerView; clear the bubble's streamed
          // preamble so it isn't shown twice (the validated narrative is authoritative).
          const ownsText = a.kind === 'prose' || a.kind === 'clarify' || a.kind === 'analysis' || a.kind === 'email_sent';
          // Server suggestions win when present (spine has none yet); else fall back to the client
          // map keyed on the rendered answer. Clarify offers its own option chips, not follow-ups.
          const server = (a as any).suggestions as string[] | undefined;
          const suggestions = a.kind === 'clarify'
            ? undefined
            : (server && server.length ? server : suggestFollowups(a));
          // The backend tags each result with the model tier it answered on (additive `meta.model`).
          const model = (a as any).meta?.model as string | undefined;
          const resolvedScope = (a as any).scope as CopilotScope | undefined;
          const resolvedContext = ((a as any).context || resolvedScope) as CopilotContext | undefined;
          if (resolvedContext) setConversationContext(resolvedContext);
          patch(assistantId, (m) => ({
            ...m,
            answer: a,
            text: ownsText ? '' : (n || m.text),
            suggestions,
            scope: resolvedScope || m.scope,
            meta: { source: 'live', model },
            status: 'done',
          }));
        },
        onError: (msg) => { resolved = true; errored = true; patch(assistantId, (m) => ({ ...m, text: msg, status: 'error' })); },
        onAbort: () => { resolved = true; patch(assistantId, (m) => ({ ...m, status: 'cancelled' })); },
        signal: ctrl.signal,
      }
    );
    if (!resolved) { errored = true; patch(assistantId, (m) => ({ ...m, text: 'No answer was returned.', status: 'error' })); }

    // Fallback: the live request errored — if this is a scripted beat, render the cached answer
    // instead of leaving an error turn. (A user-cancelled turn is left as cancelled, not patched.)
    if (errored) {
      const cached = DEMO_CACHE[normalizeQuestion(question)];
      if (cached) await renderCached(assistantId, cached);
    }
  }

  async function ask(raw: string) {
    const question = raw.trim();
    if (!question || streaming) return;
    setInput('');

    const assistantId = newId();
    const history = buildHistory(messagesRef.current);
    setMessages((prev) => [
      ...prev,
      { id: newId(), role: 'user', text: question, status: 'done' },
      { id: assistantId, role: 'assistant', text: '', status: 'streaming' },
    ]);
    await run(assistantId, question, history, contextRef.current);
  }

  /** Stop the in-flight turn: abort the request (the service's onAbort marks the turn cancelled). */
  function stop() {
    abortRef.current?.abort();
  }

  /**
   * Re-run the question that produced an errored/cancelled assistant turn. The failed assistant turn
   * is replaced in place (so the user turn isn't duplicated) and the history is rebuilt from the
   * completed pairs that preceded it.
   */
  async function retry(assistantId: string) {
    if (streaming) return;
    const msgs = messagesRef.current;
    const idx = msgs.findIndex((m) => m.id === assistantId);
    if (idx < 1) return;
    const userMsg = msgs[idx - 1];
    if (userMsg.role !== 'user') return;
    const history = buildHistory(msgs.slice(0, idx - 1));
    setMessages((prev) =>
      prev.map((m) =>
        m.id === assistantId
          ? { ...m, text: '', answer: undefined, count: undefined, status: 'streaming' }
          : m
      )
    );
    await run(assistantId, userMsg.text, history, contextRef.current);
  }

  // The conversation history is unbounded internally; only the most recent slice is rendered so the
  // count of live ECharts canvases stays bounded.
  const visible = messages.slice(-TRANSCRIPT_RENDER_CAP);

  const editPrompt = (text: string) => {
    setInput(text);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <Box
      sx={{
        // Wider chat than before, centered, with a little breathing room on the sides.
        maxWidth: 1100,
        mx: 'auto',
        px: { xs: 2, sm: 3 },
        py: 1,
        fontFamily: tokens.fontFamily,
        display: 'flex',
        flexDirection: 'column',
        // Self-bounded height: the panel caps its own height (the shared Paper hosts all tabs and
        // can't be flex-scoped to one), so the transcript scrolls inside it with the composer pinned.
        height: 'min(720px, 78dvh)',
        minHeight: 0,
        minWidth: 0,
        // ECharts and its tooltip/canvas should never be able to create a second document-level
        // scrollbar while the transcript is doing the vertical scrolling.
        overflow: 'hidden',
      }}
    >
      {/* Scrolling transcript: takes the available height, the composer stays pinned below it.
          The scrollbar is slimmed + tinted to the brand neutrals so it reads as part of the panel. */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          // When a multi-chart answer reaches the end of this region, keep wheel/touch scrolling
          // in the transcript instead of passing it to the dashboard/document underneath.
          overscrollBehavior: 'contain',
          // Reserve the scrollbar gutter so content stays centered whether or not the bar is present.
          scrollbarGutter: 'stable',
          // Thin, unobtrusive scrollbar (WebKit + Firefox), thumb darkens on hover.
          scrollbarWidth: 'thin',
          scrollbarColor: `${tokens.surface.border} transparent`,
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: tokens.surface.border,
            borderRadius: 8,
            border: '2px solid transparent',
            backgroundClip: 'padding-box',
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: tokens.text.muted },
        }}
      >
        <MessageList
          messages={visible}
          onExample={ask}
          onStop={stop}
          onRetry={retry}
          onFollowup={ask}
          onEdit={editPrompt}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={1}
            maxRows={6}
            inputRef={inputRef}
            placeholder="Ask about this study…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            // Enter sends; Shift+Enter keeps the modern-chat multiline editing affordance. MUI
            // automatically grows the textarea until maxRows, then keeps the prompt scrollable.
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                ask(input);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '12px', fontFamily: tokens.fontFamily },
              '& textarea': { overflowY: 'auto' },
            }}
          />
          <Box
            component="button"
            onClick={() => ask(input)}
            disabled={streaming}
            sx={{
              alignSelf: 'flex-end', minHeight: 40, px: 3, borderRadius: '12px', border: 'none', cursor: 'pointer',
              bgcolor: tokens.brand.cardinal, color: '#fff', fontWeight: 600, fontFamily: tokens.fontFamily,
              outline: 'none',
              '&:focus-visible': { outline: `2px solid ${tokens.brand.cardinal}`, outlineOffset: '2px' },
              '&:disabled': { opacity: 0.6, cursor: 'default' },
            }}
          >
            Ask
          </Box>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
          <Box
            component="button"
            onClick={resetChat}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 1.5, py: 0.5, borderRadius: '8px', border: `1px solid ${tokens.text.muted}`, cursor: 'pointer',
              background: tokens.surface.card, color: tokens.text.secondary,
              fontFamily: tokens.fontFamily, fontSize: '0.8125rem', fontWeight: 500,
              '&:hover': { borderColor: tokens.brand.cardinal, color: tokens.text.primary },
              outline: 'none',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="16" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Start a new chat
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
